import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Alert } from '@/components/common/Alert';
import { rolesService, Role, Permission, AVAILABLE_PERMISSIONS } from '@/services/roles.service';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/context/AuthContext';
import { XMarkIcon, PencilIcon, TrashIcon, PlusIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { isAdminUser } from '@/utils/role';
import '@/styles/dashboard.css';
import '@/styles/autorite-controle.css';

const ITEMS_PER_PAGE = 10;

export const AutoriteControle = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasPermission, loading: permissionsLoading, userRole } = usePermissions();
  
  // Admin via roleName (pas d'ID fixe)
  const isAdmin = isAdminUser(user, userRole);
  const canCreate = isAdmin || hasPermission('roles', 'create');
  const canUpdate = isAdmin || hasPermission('roles', 'update');
  const canDelete = isAdmin || hasPermission('roles', 'delete');
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as Permission[]
  });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await rolesService.getAll();
      // S'assurer que les permissions sont bien formatées
      const formattedData = (data || []).map(role => ({
        ...role,
        permissions: Array.isArray(role.permissions) ? role.permissions : []
      }));
      setRoles(formattedData);
    } catch (err: any) {
      console.error('Erreur lors du chargement des rôles:', err);
      setError('Erreur lors du chargement des rôles: ' + (err.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = useMemo(() => {
    let filtered = [...roles];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(role =>
        role.name.toLowerCase().includes(query) ||
        role.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [roles, searchQuery]);

  const totalPages = Math.ceil(filteredRoles.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedRoles = filteredRoles.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleAdd = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      permissions: []
    });
    setShowModal(true);
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name || '',
      description: role.description || '',
      permissions: Array.isArray(role.permissions) ? role.permissions : []
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(t('adminAuthority.deleteConfirm', { name }))) {
      return;
    }

    try {
      await rolesService.delete(id);
      setNotification({ type: 'success', message: t('adminAuthority.deleteSuccess') });
      await loadRoles();
    } catch (err: any) {
      setNotification({ type: 'error', message: 'Erreur lors de la suppression: ' + (err.response?.data?.error || err.message || 'Erreur inconnue') });
    }
  };

  const togglePermission = (resource: string, action: string) => {
    setFormData(prev => {
      const permissions = [...prev.permissions];
      const existingIndex = permissions.findIndex(p => p.resource === resource);

      if (existingIndex >= 0) {
        const resourcePerm = permissions[existingIndex];
        if (resourcePerm.actions.includes(action)) {
          // Retirer l'action
          resourcePerm.actions = resourcePerm.actions.filter(a => a !== action);
          // Si plus d'actions, retirer la ressource
          if (resourcePerm.actions.length === 0) {
            permissions.splice(existingIndex, 1);
          }
        } else {
          // Ajouter l'action
          resourcePerm.actions.push(action);
        }
      } else {
        // Ajouter la ressource avec l'action
        permissions.push({ resource, actions: [action] });
      }

      return { ...prev, permissions };
    });
  };

  // Fonction pour vérifier si une permission est cochée dans le formulaire
  const isFormPermissionChecked = (resource: string, action: string): boolean => {
    if (!formData.permissions || !Array.isArray(formData.permissions)) {
      return false;
    }
    const perm = formData.permissions.find(p => p && p.resource === resource);
    return perm ? (Array.isArray(perm.actions) && perm.actions.includes(action)) : false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setNotification({ type: 'warning', message: t('adminAuthority.nameRequired') });
      return;
    }

    try {
      const dataToSave = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        permissions: formData.permissions
      };

      console.log('💾 Sauvegarde rôle avec données:', dataToSave);

      if (editingRole) {
        await rolesService.update(editingRole.id, dataToSave);
        console.log('✅ Rôle mis à jour avec succès');
      } else {
        const result = await rolesService.create(dataToSave);
        console.log('✅ Rôle créé avec succès:', result);
      }
      setShowModal(false);
      setNotification({ type: 'success', message: editingRole ? t('adminAuthority.updateSuccess') : t('adminAuthority.createSuccess') });
      await loadRoles();
    } catch (err: any) {
      console.error('❌ Erreur complète:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erreur inconnue';
      const statusCode = err.response?.status || 'N/A';
      setNotification({ type: 'error', message: `Erreur lors de la sauvegarde (${statusCode}): ${errorMessage}` });
    }
  };

  const formatPermissions = (permissions: Permission[] | undefined | null): string => {
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
      return t('adminAuthority.noPermission');
    }
    return permissions.map(p => {
      if (!p || !p.resource) return '';
      const resourceLabel = AVAILABLE_PERMISSIONS[p.resource as keyof typeof AVAILABLE_PERMISSIONS]?.label || p.resource;
      const actionsCount = Array.isArray(p.actions) ? p.actions.length : 0;
      return `${resourceLabel} (${actionsCount})`;
    }).filter(Boolean).join(', ');
  };

  const columns: ResponsiveTableColumn<Role>[] = [
    {
      key: 'name',
      label: t('adminAuthority.name'),
      render: (role) => (
        <div className="table-cell-name">
          {role.name}
        </div>
      )
    },
    {
      key: 'description',
      label: t('adminAuthority.description'),
      render: (role) => (
        <div className="table-cell-description">
          {role.description || '-'}
        </div>
      ),
      mobileHidden: true
    },
    {
      key: 'permissions',
      label: t('adminAuthority.permissions'),
      render: (role) => (
        <div className="table-cell-text">
          {formatPermissions(role.permissions)}
        </div>
      ),
      mobileHidden: true
    },
    {
      key: 'actions',
      label: t('adminAuthority.actions'),
      mobileHidden: true,
      render: (role) => (
        <div className="product-actions">
          {canUpdate && (
            <button
              className="action-btn action-edit"
              onClick={() => handleEdit(role)}
              title={t('adminAuthority.edit')}
              aria-label={t('adminAuthority.editRoleAria')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg>
            </button>
          )}
          {canDelete && (
            <button
              className="action-btn action-delete"
              onClick={() => handleDelete(role.id, role.name)}
              title={t('adminAuthority.delete')}
              aria-label={t('adminAuthority.deleteRoleAria')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <>
            <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminAuthority.title', { count: filteredRoles.length })}</h3>
                <div className="flex gap-2 items-center">
                  {canCreate && (
                    <button
                      onClick={handleAdd}
                      className="btn btn-primary btn-sm"
                    >
                      <PlusIcon className="btn-icon" />
                      {t('adminAuthority.addRole')}
                    </button>
                  )}
                </div>
              </div>

              {/* Barre de recherche */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminAuthority.search')}</label>
                  <input
                    type="text"
                    placeholder={t('adminAuthority.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {notification && (
                <Alert
                  type={notification.type}
                  message={notification.message}
                  onClose={() => setNotification(null)}
                />
              )}

              {error ? (
                <Alert type="error" message={error} />
              ) : (
                <ResponsiveTable
                  columns={columns}
                  data={paginatedRoles}
                  loading={loading}
                  loadingMessage={t('adminAuthority.loading')}
                  emptyMessage={
                    searchQuery
                      ? t('adminAuthority.noSearch')
                      : t('adminAuthority.empty')
                  }
                  mobileCardTitle={(role) => (
                    <div>
                      <div className="table-cell-name mb-1">
                        {role.name}
                      </div>
                      <div className="table-cell-description">
                        {role.description || t('adminAuthority.noDescription')}
                      </div>
                    </div>
                  )}
                  mobileCardActions={(role) => (
                    <div className="product-actions" style={{ display: 'flex', gap: '8px' }}>
                      {canUpdate && (
                        <button
                          className="action-btn action-edit"
                          onClick={() => handleEdit(role)}
                          title={t('adminAuthority.edit')}
                          aria-label={t('adminAuthority.editRoleAria')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                          </svg>
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="action-btn action-delete"
                          onClick={() => handleDelete(role.id, role.name)}
                          title={t('adminAuthority.delete')}
                          aria-label={t('adminAuthority.deleteRoleAria')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                  tableClassName="products-table"
                />
              )}

              {/* Pagination */}
              {!error && totalPages > 1 && (
                <div style={{
                  padding: '20px',
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminAuthority.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredRoles.length), total: filteredRoles.length })}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        background: currentPage === 1 ? '#f5f5f5' : 'white',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: currentPage === 1 ? 0.5 : 1
                      }}
                    >
                      {t('adminAuthority.previous')}
                    </button>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              style={{
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                background: currentPage === page ? '#8B204E' : 'white',
                                color: currentPage === page ? 'white' : '#333',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: currentPage === page ? 600 : 400
                              }}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '8px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        background: currentPage === totalPages ? '#f5f5f5' : 'white',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: currentPage === totalPages ? 0.5 : 1
                      }}
                    >
                      {t('adminAuthority.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>
      {/* Modal Add/Edit */}
        {showModal && (
          <div 
            className="modal-overlay" 
            onClick={() => setShowModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <div className="modal-content autorite-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 id="modal-title" className="modal-title">{editingRole ? t('adminAuthority.modalTitle') : t('adminAuthority.modalTitleAdd')}</h2>
                <button onClick={() => setShowModal(false)} className="modal-close" aria-label={t('adminAuthority.close')}>
                  <XMarkIcon className="btn-icon" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body autorite-modal-body">
                  <div className="form-group">
                    <label className="form-label">{t('adminAuthority.roleName')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder={t('adminAuthority.roleNamePlaceholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('adminAuthority.description')}</label>
                    <textarea
                      className="form-textarea"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      placeholder={t('adminAuthority.descriptionPlaceholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('adminAuthority.permissions')}</label>
                    <div className="permissions-container">
                      {Object.entries(AVAILABLE_PERMISSIONS).map(([resource, config]) => (
                        <div key={resource} className="permission-resource">
                          <div className="permission-resource-title">
                            {config.label}
                          </div>
                          <div className="permission-actions">
                            {config.actions.map(action => (
                              <label
                                key={action.key}
                                className={`permission-checkbox-label ${isFormPermissionChecked(resource, action.key) ? 'active' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isFormPermissionChecked(resource, action.key)}
                                  onChange={() => togglePermission(resource, action.key)}
                                />
                                <span>{action.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="modal-footer autorite-modal-footer">
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    {t('adminAuthority.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingRole ? t('adminAuthority.save') : t('adminAuthority.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
    </>
  );
};
